import { z } from 'zod'
import type { JourneyRunOutcome } from './run-outcome.ts'
import { isFailedOutcome } from './run-status.ts'

export const JourneyFailureCodeSchema = z.enum([
  'AppUnavailable', 'RateLimited', 'CredentialsNotProvided', 'ProvidedCredentialsRejected',
  'InvitationRequired', 'AdminPermissionRequired', 'PaidAccessRequired',
  'RequiredDataNotProvided', 'ExternalHandoffBlocked',
  'DeploymentProtection', 'BotProtection', 'Captcha', 'OneTimeCodeRequired',
  'LocalConnectionRefused',
])
export type JourneyFailureCode = z.infer<typeof JourneyFailureCodeSchema>
// Codes available without measured refused-host evidence.
export const ClassifiedFailureCodeSchema = JourneyFailureCodeSchema.exclude(['LocalConnectionRefused'])

export const FAILURE_CODE_LABELS = {
  AppUnavailable: 'App unavailable',
  RateLimited: 'Rate limited',
  CredentialsNotProvided: 'Credentials not provided',
  ProvidedCredentialsRejected: 'Credentials rejected',
  InvitationRequired: 'Invitation required',
  AdminPermissionRequired: 'Admin permission required',
  PaidAccessRequired: 'Paid access required',
  RequiredDataNotProvided: 'Required data not provided',
  ExternalHandoffBlocked: 'External handoff blocked',
  DeploymentProtection: 'Deployment protection',
  BotProtection: 'Bot protection',
  Captcha: 'CAPTCHA',
  OneTimeCodeRequired: 'One-time code required',
  LocalConnectionRefused: 'Local connection refused',
} as const satisfies Record<JourneyFailureCode, string>

const ClassificationDetailSchema = z.string().transform(value => value.replace(/\0/g, '').trim()).pipe(z.string().min(1).max(2000))
export const JourneyFailureClassificationSchema = z.union([
  z.object({
    // Null means no supported external blocker was established, not a generic failure code.
    statusCode: ClassifiedFailureCodeSchema.nullable(),
    classificationDetail: ClassificationDetailSchema,
  }),
  z.object({
    statusCode: z.literal('LocalConnectionRefused'),
    refusedHost: z.string().min(1),
    classificationDetail: ClassificationDetailSchema,
  }),
])
export type JourneyFailureClassification = z.infer<typeof JourneyFailureClassificationSchema>
const JourneyFailureMetadataSchema = z.object({
  statusCode: JourneyFailureCodeSchema,
  classificationDetail: ClassificationDetailSchema.optional(),
})
export interface JourneyFailureMetadata {
  statusCode?: JourneyFailureCode
  classificationDetail?: string
}

/** Only supported external codes survive the reporting boundary; removed codes are not remapped. */
export function parseFailureMetadata(raw: unknown): JourneyFailureMetadata {
  const parsed = JourneyFailureMetadataSchema.safeParse(raw)
  return parsed.success ? parsed.data : {}
}

/** App-load failures have one code. Other failures require evidence or remain uncoded. */
export function withFailureStatus(outcome: JourneyRunOutcome): JourneyRunOutcome {
  if (!isFailedOutcome(outcome)) return outcome
  const { statusCode, classificationDetail, ...base } = outcome
  const metadata = outcome.status === 'infra' ? {} : parseFailureMetadata(outcome)
  if (outcome.status === 'blocked' && (outcome.reason === 'target-url-invalid' || outcome.reason === 'target-unreachable')) {
    metadata.statusCode = metadata.statusCode === 'RateLimited' ? 'RateLimited' : 'AppUnavailable'
  }
  if (statusCode === metadata.statusCode && classificationDetail === metadata.classificationDetail) return outcome
  return { ...base, ...metadata }
}
